import { Unit } from "types/landlord/address";
import { SectionCard } from "shared/app/SectionCard";
import { Row } from "views/applications-id-report/components/Row";
import { useState } from "react";
import { Button, ButtonSize, ButtonVariant, Show } from "shared/components";
import { useFormTyped } from "shared/hooks";
import { SaveCancelButtons } from "shared/app/SaveCancelButtons";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { getErrorMessage } from "utils/error";
import addressApi from "api/landlord/address";
import { type Register } from "shared/hooks";
import { PlanForm } from "shared/app/PlanForm";
import { useAuthContext } from "providers/Auth";
import { PmsSyncOption } from "shared/app/RentalInfoForm/components/PmsSyncOption";
import { PMSValue } from "shared/app/RentalInfoForm/components/PmsValue";
import { BaseInput } from "shared/components/forms/BaseInput";
import { numberParser } from "shared/components/forms/BaseInput/parsers";
import { useEditDetailsPermissions } from "shared/hooks/use-edit-details-permissions";

interface PlanSectionProps {
  unit: Unit;
}

interface PmsEditPlanFormValues {
  sqft?: number;
  beds?: number;
  baths?: number;
  dimensions?: string;
  pms_overrides: {
    sqft?: boolean;
    beds?: boolean;
    baths?: boolean;
    dimensions?: boolean;
  };
}

const PmsEditPlanForm = ({
  onClose,
  unit,
  initialValues
}: {
  onClose: () => void;
  unit: Unit;
  initialValues: PmsEditPlanFormValues;
}) => {
  const queryClient = useQueryClient();
  const { type } = useAuthContext();
  const { register, onSubmit, values } = useFormTyped(initialValues);
  const overrides = values.pms_overrides;

  const { mutate: updatePlan, isPending } = useMutation({
    mutationFn: () =>
      addressApi.editUnit(unit.id, {
        plan: {
          sqft: values.sqft,
          beds: values.beds,
          baths: values.baths
        },
        dimensions: values.dimensions || undefined,
        pms_overrides: {
          ...unit.pms_overrides,
          ...values.pms_overrides
        }
      }),
    onSuccess: () => {
      toast.success("Plan updated");
      queryClient.invalidateQueries({
        queryKey: ["unit", String(unit.property.id), String(unit.id)]
      });
      onClose();
    },
    onError: (error) => {
      toast.error(`Failed to update plan: ${getErrorMessage(error)}`);
    }
  });

  const handleSubmit = onSubmit({
    onSuccess: () => updatePlan()
  });

  const handleOverrideChange = (
    field: "sqft" | "beds" | "baths" | "dimensions",
    synced: boolean
  ) => {
    const newOverrides = { ...overrides, [field]: synced };
    register("pms_overrides").onChange(newOverrides);

    if (synced && unit.pms_values) {
      const pmsVal = unit.pms_values[field];
      register(field).onChange(pmsVal ?? null);
    }
  };

  return (
    <SectionCard
      title="Plan"
      footer={
        <SaveCancelButtons
          onSave={(e) => handleSubmit(e)}
          onCancel={onClose}
          saveLoading={isPending}
        />
      }
    >
      <div className="space-y-8">
        <div className="grid grid-cols-2 gap-4 items-center">
          <div>
            <BaseInput
              id="sqft"
              label="Size, sq.ft"
              placeholder={(overrides?.sqft ?? true) ? "-" : "240"}
              parser={numberParser({ max: 10000 })}
              readOnly={overrides?.sqft ?? true}
              className={
                (overrides?.sqft ?? true) ? "[&_.bg-white]:!bg-alpha-40" : ""
              }
              value={register("sqft").value ?? null}
              onChange={(val) => register("sqft").onChange(val)}
            />
            <PmsSyncOption
              value={overrides?.sqft ?? true}
              onChange={(val) => handleOverrideChange("sqft", val)}
              name="sqft"
            />
          </div>
          <PMSValue
            value={
              unit.pms_values?.sqft != null
                ? String(unit.pms_values.sqft)
                : undefined
            }
          />
        </div>

        <div className="grid grid-cols-2 gap-4 items-center">
          <div>
            <BaseInput
              id="beds"
              label="Beds"
              placeholder={(overrides?.beds ?? true) ? "-" : "1"}
              parser={numberParser({ max: 10 })}
              readOnly={overrides?.beds ?? true}
              className={
                (overrides?.beds ?? true) ? "[&_.bg-white]:!bg-alpha-40" : ""
              }
              value={register("beds").value ?? null}
              onChange={(val) => register("beds").onChange(val)}
            />
            <PmsSyncOption
              value={overrides?.beds ?? true}
              onChange={(val) => handleOverrideChange("beds", val)}
              name="beds"
            />
          </div>
          <PMSValue
            value={
              unit.pms_values?.beds != null
                ? String(unit.pms_values.beds)
                : undefined
            }
          />
        </div>

        <div className="grid grid-cols-2 gap-4 items-center">
          <div>
            <BaseInput
              id="baths"
              label="Baths"
              placeholder={(overrides?.baths ?? true) ? "-" : "2"}
              parser={numberParser({ max: 10 })}
              readOnly={overrides?.baths ?? true}
              className={
                (overrides?.baths ?? true) ? "[&_.bg-white]:!bg-alpha-40" : ""
              }
              value={register("baths").value ?? null}
              onChange={(val) => register("baths").onChange(val)}
            />
            <PmsSyncOption
              value={overrides?.baths ?? true}
              onChange={(val) => handleOverrideChange("baths", val)}
              name="baths"
            />
          </div>
          <PMSValue
            value={
              unit.pms_values?.baths != null
                ? String(unit.pms_values.baths)
                : undefined
            }
          />
        </div>

        <Show if={type.isSitesAndCommunities}>
          <div className="grid grid-cols-2 gap-4 items-center">
            <div>
              <BaseInput
                id="dimensions"
                label="Dimensions"
                placeholder={(overrides?.dimensions ?? true) ? "-" : "16 x 66"}
                readOnly={overrides?.dimensions ?? true}
                className={
                  (overrides?.dimensions ?? true)
                    ? "[&_.bg-white]:!bg-alpha-40"
                    : ""
                }
                value={register("dimensions").value ?? ""}
                onChange={(val) => register("dimensions").onChange(val)}
              />
              <PmsSyncOption
                value={overrides?.dimensions ?? true}
                onChange={(val) => handleOverrideChange("dimensions", val)}
                name="dimensions"
              />
            </div>
            <PMSValue value={unit.pms_values?.dimensions ?? undefined} />
          </div>
        </Show>
      </div>
    </SectionCard>
  );
};

const EditPlanForm = ({
  onClose,
  unit,
  initialValues
}: {
  onClose: () => void;
  unit: Unit;
  initialValues: {
    sqft?: number;
    beds?: number;
    baths?: number;
    dimensions?: string;
  };
}) => {
  const queryClient = useQueryClient();
  const { register, onSubmit, values } = useFormTyped(initialValues);

  const { mutate: updatePlan, isPending } = useMutation({
    mutationFn: () =>
      addressApi.editUnit(unit.id, {
        plan: {
          sqft: values.sqft,
          beds: values.beds,
          baths: values.baths
        },
        dimensions: values.dimensions || undefined
      }),
    onSuccess: () => {
      toast.success("Plan updated");
      queryClient.invalidateQueries({
        queryKey: ["unit", String(unit.property.id), String(unit.id)]
      });
      onClose();
    },
    onError: (error) => {
      toast.error(`Failed to update plan: ${getErrorMessage(error)}`);
    }
  });

  const handleSubmit = onSubmit({
    onSuccess: () => updatePlan()
  });

  return (
    <PlanForm
      register={register as unknown as Register}
      footer={
        <SaveCancelButtons
          onSave={(e) => handleSubmit(e)}
          onCancel={onClose}
          saveLoading={isPending}
        />
      }
    />
  );
};

export const PlanSection = ({ unit }: PlanSectionProps) => {
  const [isEditing, setIsEditing] = useState(false);
  const { type } = useAuthContext();
  const { canEditUnitDetails, unitEditDeniedTooltip } =
    useEditDetailsPermissions();
  const isPmsUnit = type.isPmsListingSyncEnabled && !!unit.pms_id;

  if (isEditing) {
    if (isPmsUnit)
      return (
        <PmsEditPlanForm
          onClose={() => setIsEditing(false)}
          unit={unit}
          initialValues={{
            sqft: unit.plan.sqft,
            beds: unit.plan.beds,
            baths: unit.plan.baths,
            dimensions: unit.dimensions,
            pms_overrides: {
              sqft: unit.pms_overrides?.sqft ?? true,
              beds: unit.pms_overrides?.beds ?? true,
              baths: unit.pms_overrides?.baths ?? true,
              dimensions: unit.pms_overrides?.dimensions ?? true
            }
          }}
        />
      );

    return (
      <EditPlanForm
        onClose={() => setIsEditing(false)}
        unit={unit}
        initialValues={{
          sqft: unit.plan.sqft,
          beds: unit.plan.beds,
          baths: unit.plan.baths,
          dimensions: unit.dimensions
        }}
      />
    );
  }

  return (
    <SectionCard
      title="Plan"
      headerExtra={
        <Button
          size={ButtonSize.SM}
          variant={ButtonVariant.BASE}
          onClick={() => setIsEditing(true)}
          disabled={!canEditUnitDetails}
          tooltip={unitEditDeniedTooltip}
          tooltipPlacement="bottom-end"
        >
          Edit
        </Button>
      }
    >
      <div className="space-y-4">
        <Row title="Size, sq.ft" value={unit.plan.sqft} />
        <Row title="Beds" value={unit.plan.beds} />
        <Row title="Baths" value={unit.plan.baths} />
        <Show if={type.isSitesAndCommunities}>
          <Row title="Dimensions" value={unit.dimensions} />
        </Show>
      </div>
    </SectionCard>
  );
};
